import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Observable, from, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, finalize, switchMap, filter, take, takeUntil, tap, map } from 'rxjs/operators';
import { SpinnerService } from './spinner.service';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SessionService } from './session.service';
import { AuthService } from './auth.service';
import { RequestCancelService } from './request-cancel.service';
import { ApiErrorNotifierService, recordRequestDuration } from './api-error-notifier.service';
import { recordDataRequest } from './no-data-notifier.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class MyInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  // Renew anything expiring inside this window instead of sending it. A token
  // with seconds left dies in flight, and a client clock running behind the
  // server hides the expiry from Amplify entirely — both surface as a 401.
  private readonly TOKEN_REFRESH_MARGIN_SECONDS = 120;
  private readonly AI_CHAT_URL = environment.aiChatEndPoint;
  private readonly AI_CHATBOT_URL = environment.aiChatbotEndPoint;
  private readonly AI_ASYNC_INVOKE_URL = environment.aiAsyncInvokeEndPoint;
  private readonly AI_ASYNC_POLL_URL = environment.aiAsyncPollEndPoint;
  private readonly AI_EXECUTE_SQL_URL = environment.aiExecuteSqlEndPoint;
  private readonly AI_ACTION_SQL_URL = environment.aiActionSqlEndPoint;

  /**
   * Third-party hosts we call in the background, whose failures are never the
   * user's problem to solve.
   *
   * Nominatim reverse-geocodes a lat/lon into a city name to enrich analytics
   * events and the login/idle audit trail. The user never asked for it, cannot
   * act on it, and nothing on screen depends on it — it is also rate-limited and
   * best-effort by design, so it fails routinely. A toast about it is pure noise.
   */
  private readonly BACKGROUND_HOSTS = ['nominatim.openstreetmap.org'];

  /**
   * Requests identified by these `api_key` values never raise a toast.
   *
   * 100410 is the alerts/notifications feed behind the bell and the filter bar.
   * Its callers already handle failure by rendering an empty list, and it loads
   * on its own schedule rather than in response to anything the user did — so a
   * toast about it interrupts work it has nothing to do with.
   *
   * Keyed on the body rather than the URL because these all post to the same
   * shared endpoint as everything else; the api_key is what distinguishes them.
   */
  private readonly SILENT_API_KEYS = new Set(['100410', '100280']);

  /**
   * Endpoints of ours whose failures the user should never hear about.
   *
   * `pss/get-preferences` answers with an empty body when a widget has no saved
   * preference — the normal case, not a fault. Angular cannot parse that as the
   * JSON it asked for, so it arrives as an error carrying a 200, which the copy
   * layer can only read as a server fault and toasts as one. Every caller
   * already treats a missing preference as "use the defaults", so there is
   * nothing here for the user to do and nothing worth saying.
   *
   * Matched on the URL because these are endpoints of their own, not `api_key`
   * values posted to the shared one.
   */
  private readonly SILENT_ENDPOINTS = ['pss/get-preferences', 'api/bookmarks/get-bookmark-unread-count', 'api/notes/notes-data', 'export/get-help-url'];

  constructor(
    private spinnerService: SpinnerService, 
    private sessionService: SessionService, 
    private authService: AuthService, 
    private cancelService: RequestCancelService,
    private apiErrorNotifier: ApiErrorNotifierService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

    // Only JSON - API Like GET POST 
    if (request.url.includes("/api/meetings/guest/")) return next.handle(request);
    if (request.url.includes("/api/secure-messages/")) return next.handle(request);
    // SignalR negotiate/WebSocket for practice (anonymous) + FRM secure messaging hub.
    if (request.url.includes("/secureMessageHub")) return next.handle(request);

    if(request.responseType!="json") return next.handle(request)

    // Checking User
    if(!this.authService.isLoggedIn()) {
      return request.url.includes("/api/meetings/") ? this.withMeetingToken(request, next) : next.handle(request);
    }

  // A blocked error response tells us nothing about itself, so how long the
  // request ran is the only way to tell a gateway timeout from a connection
  // that was never made. Stamped onto the error below for the copy layer.
  const startedAt = Date.now();

    const session$ = this.getIdToken().pipe(
      switchMap((token) => {
        if (!token) {
          // No token even after a forced refresh — the Cognito session is gone,
          // so every call from here on would 401. Send the user to login once
          // instead of letting the whole page fail request by request.
          this.authService.logoutAndRedirect();
          return throwError(() => 'Unauthorized');
        }
        return this.handleRequestWithToken(request, next, token);
      }),
      tap((event) => {
        // The endpoint and the payload are visible here and nowhere else: a
        // chart reports "no data" from an `@Input` setter, long past the point
        // where it could say which call filled it. Recording each response as
        // it lands is what lets the no-data log show the query behind an empty
        // screen. Background third-party lookups are skipped — nothing on the
        // dashboard is drawn from them.
        if (event instanceof HttpResponse && !this.isBackgroundRequest(request.url)) {
          recordDataRequest(request.urlWithParams, request.body, event.body);
        }
      }),
      catchError((error: HttpErrorResponse|any) => {
        // Recorded before anything else returns, so both the toast and the
        // components' own `friendlyApiErrorMessage` see the same duration.
        const elapsedMs = Date.now() - startedAt;
        recordRequestDuration(error, elapsedMs);
        this.apiErrorNotifier.recordFailure();

        const silent = this.isSilentFailure(request);

        // Logged before any of the early returns below, so a failure that
        // raises no toast still leaves a trace. Silenced requests log at debug
        // level rather than error: nothing here needs acting on, and a red
        // console.error says the opposite. The detail stays reachable under the
        // Verbose filter, which is how a misconfigured feed gets diagnosed.
        this.logFailure(request, error, elapsedMs, 'request', silent);

        if (error.status === 401) {
          return this.handle401Error(request, next)
          .pipe(takeUntil(this.cancelService.cancelSignal$));;
        }

        // when No current user — we are already redirecting to login, so a
        // toast about the failed call on the way out would only be noise.
        if(error=='No current user' || error?.message=='No current user') {
          this.authService.logoutAndRedirect();
          return throwError(() => error);
        }

        // Surface the failure to the user, unless this is a request whose
        // failure the user should not hear about.
        if (!silent) {
          this.apiErrorNotifier.notifyFailure(error);
        }

        return throwError(() => error);
      }),
      finalize(() => this.spinnerService.hide())
    );

    // Keep floating-chat AI requests alive across module navigation cancellations.
    if (this.isAiChatRequest(request.url)) {
      return session$;
    }

    return session$.pipe(
      takeUntil(this.cancelService.cancelSignal$),
    );
  }

  /**
   * Current ID token, renewed when it is expired or close to it.
   *
   * Amplify only renews on a token it already considers expired, which leaves
   * two holes: a token in its last seconds is handed out and expires before the
   * response lands, and a slow client clock makes an expired token look valid.
   * Both were unreachable while sessions ended after 20 idle minutes; at 120
   * they are routine, so refresh inside a margin rather than on expiry alone.
   */
  private getIdToken(forceRefresh = false): Observable<string> {
    return from(fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined)).pipe(
      switchMap((session) => {
        const idToken = session?.tokens?.idToken;
        const token = idToken?.toString() ?? '';

        if (forceRefresh || (token && !this.isExpiringSoon(idToken))) {
          return of(token);
        }

        return from(fetchAuthSession({ forceRefresh: true })).pipe(
          map((refreshed) => refreshed?.tokens?.idToken?.toString() ?? '')
        );
      })
    );
  }

  /**
   * A meeting call from a tab with no login of its own.
   *
   * The meeting page is a tab of its own — the Visit Planner opens it with
   * `noopener`, and the host may follow the invite email — so this tab's
   * sessionStorage login is not there and `isLoggedIn()` is false. Amplify keeps
   * the Cognito session in localStorage, so the token is still available, and
   * the meetings API identifies the caller only from it: without it the host's
   * "End meeting for all" is refused as "Only the meeting host can do that."
   * Only the token is attached; a guest with no Cognito session is sent as is.
   */
  private withMeetingToken(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.getIdToken().pipe(
      catchError(() => of('')),
      switchMap((token) =>
        next.handle(token ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : request)
      )
    );
  }

  private isExpiringSoon(idToken: any): boolean {
    const exp = idToken?.payload?.exp;
    if (typeof exp !== 'number') return false;
    return exp - Math.floor(Date.now() / 1000) <= this.TOKEN_REFRESH_MARGIN_SECONDS;
  }
/**
   * Whether a failed request should be reported to the user at all.
   *
   * Four reasons it should not. AI chat components render their own inline
   * error state, so a global toast on top would report the same failure twice.
   * Background third-party lookups are not part of any action the user took.
   * Some of our own feeds poll on their own schedule with a caller that already
   * handles failure, which is what {@link SILENT_API_KEYS} names. And a few of
   * our own endpoints fail as a matter of routine with nothing for the user to
   * act on, which is what {@link SILENT_ENDPOINTS} names.
   *
   * This governs the toast only — the error is still rethrown to the caller's
   * `catchError` and still logged, so nothing is lost for support.
   */
  private isSilentFailure(request: HttpRequest<unknown>): boolean {
    return (
      this.isAiChatRequest(request.url) ||
      this.isBackgroundRequest(request.url) ||
      this.isSilentEndpoint(request.url) ||
      this.isSilentApiKey(request.body)
    );
  }

  /**
   * Logs a failed request: what was called, what came back, and what was sent.
   *
   * The toast deliberately says nothing internal, and some failures raise no
   * toast at all, so this is the only place the detail survives for whoever has
   * to diagnose it. Kept as one `console.error` with a readable first line and
   * an expandable detail object, which is what devtools renders best.
   */
  private logFailure(
    request: HttpRequest<unknown>,
    error: unknown,
    elapsedMs: number | null,
    phase = 'request',
    silent = false
  ): void {
    const status = error instanceof HttpErrorResponse ? error.status : null;
    const timing = elapsedMs === null ? '' : ` after ${elapsedMs}ms`;
    const write = silent ? console.debug.bind(console) : console.error.bind(console);

    write(
      `[API] ${phase} failed${status === null ? '' : ` (${status})`}: ` +
        `${request.method} ${request.urlWithParams}${timing}`,
      {
        endpoint: request.urlWithParams,
        method: request.method,
        status,
        statusText: error instanceof HttpErrorResponse ? error.statusText : null,
        message: this.messageOf(error),
        // The payload that provoked it — the first thing worth seeing when a
        // 400 or a 422 needs explaining.
        payload: request.body,
        // Whatever the server actually said, untouched by the copy layer.
        response: error instanceof HttpErrorResponse ? error.error : null,
        elapsedMs,
        error,
      }
    );
  }

  /** The most useful one-line description of a thrown value. */
  private messageOf(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }
    const message = (error as { message?: unknown } | null)?.message;
    return typeof message === 'string' ? message : String(error);
  }

  private isSilentApiKey(body: unknown): boolean {
    const key = this.apiKeyOf(body);
    return key !== null && this.SILENT_API_KEYS.has(key);
  }

  /** The `api_key` a request body declares, however that body is encoded. */
  private apiKeyOf(body: unknown): string | null {
    if (!body) {
      return null;
    }

    if (body instanceof FormData) {
      const value = body.get('api_key');
      return typeof value === 'string' ? value : null;
    }

    // Bodies are usually still plain objects at this point, but a caller that
    // pre-serialised its own JSON should not slip past on a technicality.
    let candidate: unknown = body;
    if (typeof body === 'string') {
      try {
        candidate = JSON.parse(body);
      } catch {
        return null;
      }
    }

    if (!candidate || typeof candidate !== 'object') {
      return null;
    }

    const value = (candidate as Record<string, unknown>)['api_key'];
    if (typeof value === 'string') {
      return value;
    }
    return typeof value === 'number' ? String(value) : null;
  }

  private isBackgroundRequest(url: string): boolean {
    return this.BACKGROUND_HOSTS.some((host) => url.includes(host));
  }

  private isSilentEndpoint(url: string): boolean {
    return this.SILENT_ENDPOINTS.some((endpoint) => url.includes(endpoint));
  }

  private isAiChatRequest(url: string): boolean {
    return (
      url === this.AI_CHAT_URL ||
      url === this.AI_CHATBOT_URL ||
      url === this.AI_ASYNC_INVOKE_URL ||
      url === this.AI_ASYNC_POLL_URL ||
      url === this.AI_EXECUTE_SQL_URL ||
      url === this.AI_ACTION_SQL_URL
    );
  }

  private handleRequestWithToken(request: HttpRequest<unknown>, next: HttpHandler, token: string): Observable<HttpEvent<unknown>> {


    if (this.authService.isLoggedIn()) {
      // Presigned S3 URLs are self-authenticating — skip all header modifications
      if (request.url.includes('X-Amz-Signature')) {
        this.spinnerService.show();
        return next.handle(request);
      }

      // Third-party background hosts, for the same reason and one more.
      //
      // They are not ours to authenticate to: attaching the Cognito id token
      // and the username would hand a public geocoding service credentials and
      // a user identifier it has no business holding. And `user_id` /
      // `schema_type` are non-safelisted headers, so on a cross-origin GET they
      // force a CORS preflight that host never agreed to answer — which is
      // enough on its own to fail a request that works fine unmodified.
      if (this.isBackgroundRequest(request.url)) {
        return next.handle(request);
      }

      if (request.url === this.AI_CHAT_URL || request.url === this.AI_CHATBOT_URL
        || request.url === this.AI_ASYNC_INVOKE_URL || request.url === this.AI_ASYNC_POLL_URL
        || request.url === this.AI_EXECUTE_SQL_URL || request.url === this.AI_ACTION_SQL_URL) {
        // 🧩 Request for AI Chat endpoints - include Authorization for CORS
        //
        // Authorization and Content-Type ONLY. The else-branch below also sends
        // `user_id` and `schema_type`, and those are custom headers: they make the
        // browser send a CORS preflight and then require the endpoint to name them
        // back in Access-Control-Allow-Headers. The portal's own API does; these AI
        // endpoints do not, so a request that took the else-branch fails preflight
        // and surfaces as a CORS error before the body is ever sent.
        //
        // AI_ACTION_SQL_URL is a Lambda Function URL rather than API Gateway, which
        // makes it the strictest of the set — its CORS allow-list is configured on
        // the function URL itself.
        request = request.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

      } else {

        const isFormData = request.body instanceof FormData;

        const headers: { [header: string]: string } = {
          'Authorization': `Bearer ${token}`,
          'user_id': this.sessionService.getUsername()
        };

        // Active schema selection (Primary/Secondary) chosen in the top nav.
        // Only sent when the current page/tab actually has schema checks; if it
        // doesn't, the value is removed from session and no header is attached.
        const schemaType = sessionStorage.getItem('schema_type');
        if (schemaType) {
          headers['schema_type'] = schemaType;
        }

        if (!isFormData) {
          headers['Content-Type'] = 'application/json';
        }
        request = request.clone({
          setHeaders: headers
        });
      }

    }

    this.spinnerService.show();
    return next.handle(request);
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      // forceRefresh, otherwise Amplify returns the same cached token the
      // server just rejected and the retry fails exactly like the first try.
      return this.getIdToken(true).pipe(
        // Sits above the retry on purpose: only a failed *refresh* signs the
        // user out. A 401 from the retried call itself is the endpoint's
        // answer, not a dead session, and must pass through untouched.
        catchError(() => {
          // Clear the flag, or every later 401 waits forever on a refresh that
          // already failed. Empty string (not null) releases queued requests.
          this.isRefreshing = false;
          this.refreshTokenSubject.next('');
          this.authService.logoutAndRedirect();
          return throwError(() => 'Unauthorized');
        }),
        switchMap((token) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token);

          if (!token) {
            this.authService.logoutAndRedirect();
            return throwError(() => 'Unauthorized');
          }
          return this.handleRequestWithToken(request, next, token);
        }),
        catchError((error) => {
          // Handle refresh token failure (e.g., redirect to login page).
          // The original 401 was swallowed so this retry could run; now that
          // the retry has failed too the user is told their session is over,
          // which otherwise happened on no screen at all.
          this.isRefreshing = false;
          this.logFailure(request, error, null, 'token refresh');
          this.apiErrorNotifier.notifyFailure(error);
          return throwError('Unauthorized');
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token: string) => {
          // Retry with the refreshed token — this used to send the username,
          // which every request in the queue then failed on.
          if (!token) return throwError(() => 'Unauthorized');
          return this.handleRequestWithToken(request, next, token);
        })
      );
    }
  }
}
